import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Home as HomeIcon,
  ThermometerSun,
  Zap,
  Save,
  FileDown,
  ArrowLeft,
  MapPin,
  Building2,
  Cpu,
  ShieldCheck,
  LogIn,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

import FranceMap from "../components/FranceMap";
import DepartmentSelector from "../components/DepartmentSelector";
import ResultsPanel from "../components/ResultsPanel";
import HeatingCurve from "../components/HeatingCurve";
import AddressAutocomplete from "../components/AddressAutocomplete";
import PacSelector from "../components/PacSelector";
import {
  ISOLATION_COEFFICIENTS,
  EMETTEURS,
  LEGACY_EMETTEURS,
  ALTITUDE_OPTIONS,
  DEPARTEMENT_TO_ZONE,
  DEPARTEMENT_NAMES,
  computeDimensionnement,
} from "../lib/climate";
import { createProject, getProject, updateProject } from "../lib/api";
import { useAuth, loginWithGoogle } from "../lib/auth";
import { saveDraft, loadDraft, clearDraft } from "../lib/draft";
import PdfPreviewModal, { buildPdfPreviewUrl, buildPdfDownloadUrl } from "../components/PdfPreviewModal";

const DEFAULT_INPUTS = {
  surface: 120,
  hauteur: 2.5,
  isolation: "RT2012",
  t_interieur: 19,
  zone: "D",
  altitude: 200,
  emetteur: "basse_temp",
  departement: "",
};

const STEPS = [
  { id: "batiment", label: "Bâtiment", icon: HomeIcon },
  { id: "climat", label: "Climat", icon: ThermometerSun },
  { id: "emetteurs", label: "Émetteurs", icon: Zap },
  { id: "pac", label: "PAC", icon: Cpu },
];

export default function Calculator() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { user } = useAuth();
  const [authPromptOpen, setAuthPromptOpen] = useState(null); // 'save' | 'export' | null

  const [meta, setMeta] = useState({
    name: "", client_name: "", address: "", postal_code: "", city: "",
    pac_id: null, deperditions_override_kw: null,
  });
  const [inputs, setInputs] = useState(DEFAULT_INPUTS);
  // Plage de couverture (locale, non persistée) — modifie l'affichage des
  // puissances dans le panneau et le filtre des PAC compatibles.
  const [couvertureRange, setCouvertureRange] = useState("100-120");
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [pdfModal, setPdfModal] = useState({ open: false, previewUrl: null, downloadUrl: null, filename: null });

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const project = await getProject(id);
        setMeta({
          name: project.name || "",
          client_name: project.client_name || "",
          address: project.address || "",
          postal_code: project.postal_code || "",
          city: project.city || "",
          pac_id: project.pac_id || null,
          deperditions_override_kw: project.deperditions_override_kw ?? null,
        });
        // Map legacy emetteur values to the current 2-option scheme.
        const projInputs = { ...project.inputs };
        if (projInputs.emetteur && LEGACY_EMETTEURS[projInputs.emetteur]) {
          projInputs.emetteur = LEGACY_EMETTEURS[projInputs.emetteur];
        }
        setInputs(projInputs);

        // Legacy migration: auto-fill city from postal code via BAN
        // if the project predates the city field.
        const postal = project.postal_code;
        if (!project.city && postal && /^\d{5}$/.test(postal)) {
          try {
            const res = await fetch(
              `https://api-adresse.data.gouv.fr/search/?q=${postal}&type=municipality&limit=1`,
            );
            const data = await res.json();
            const c = data?.features?.[0]?.properties?.city
              || data?.features?.[0]?.properties?.name;
            if (c) setMeta((m) => (m.city ? m : { ...m, city: c }));
          } catch (_) { /* ignore */ }
        }
      } catch (e) {
        toast.error("Impossible de charger le projet");
        navigate("/");
      }
    })();
  }, [id, isEdit, navigate]);

  // Restore an in-progress draft after OAuth round-trip (anonymous user
  // clicked Save/Export → logged in → returned). Only on /calculator (no id).
  useEffect(() => {
    if (isEdit) return;
    const draft = loadDraft();
    if (!draft) return;
    if (draft.meta) setMeta((m) => ({ ...m, ...draft.meta }));
    if (draft.inputs) setInputs((i) => ({ ...i, ...draft.inputs }));
    if (draft.couvertureRange) setCouvertureRange(draft.couvertureRange);
    clearDraft();
    if (user && draft.intent === "save") {
      toast.info("Brouillon restauré — cliquez sur Enregistrer pour sauvegarder.");
    } else if (user && draft.intent === "export") {
      toast.info("Brouillon restauré — cliquez sur Exporter PDF.");
    } else {
      toast.info("Brouillon restauré.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit]);

  const results = useMemo(() => computeDimensionnement(inputs), [inputs]);

  // Recalcule Min / Recommandé / Max selon la plage de couverture choisie.
  const rangedResults = useMemo(() => {
    if (!results) return results;
    const [minPct, maxPct] = couvertureRange.split("-").map(Number);
    const dep = results.deperditions_kw;
    const minKw = +(dep * minPct / 100).toFixed(2);
    const maxKw = +(dep * maxPct / 100).toFixed(2);
    return {
      ...results,
      puissance_min_kw: minKw,
      puissance_min_w: Math.round(minKw * 1000),
      puissance_totale_kw: maxKw, // borne haute = recommandée
      puissance_totale_w: Math.round(maxKw * 1000),
      puissance_max_kw: maxKw,
      puissance_max_w: Math.round(maxKw * 1000),
      couverture_pct: maxPct,
      couverture_min_pct: minPct,
      couverture_max_pct: maxPct,
      ballon_tampon_l: Math.round(maxKw * 15),
    };
  }, [results, couvertureRange]);

  const setInput = (key, value) => setInputs((prev) => ({ ...prev, [key]: value }));

  const handleSelectZone = (zone) => setInput("zone", zone);

  const handleSelectDept = (code, zone) => {
    setInputs((prev) => ({ ...prev, departement: code, zone: zone || prev.zone }));
    toast.success(`${DEPARTEMENT_NAMES[code]} (${code}) — Zone ${zone}`);
  };

  const handlePostalChange = (postal) => {
    setMeta((prev) => ({ ...prev, postal_code: postal }));
    if (postal && postal.length >= 2) {
      let dept = postal.substring(0, 2);
      if (dept === "20") {
        const num = parseInt(postal.substring(0, 5), 10);
        dept = !isNaN(num) && num >= 20200 ? "2B" : "2A";
      }
      const zone = DEPARTEMENT_TO_ZONE[dept];
      if (zone) {
        setInputs((prev) => ({ ...prev, departement: dept, zone }));
      }
    }
    // Auto-fill city from postal code via BAN (only if empty)
    if (postal && postal.length === 5 && /^\d{5}$/.test(postal)) {
      fetch(`https://api-adresse.data.gouv.fr/search/?q=${postal}&type=municipality&limit=1`)
        .then((r) => r.json())
        .then((data) => {
          const city = data?.features?.[0]?.properties?.city
            || data?.features?.[0]?.properties?.name;
          if (city) {
            setMeta((m) => (m.city ? m : { ...m, city }));
          }
        })
        .catch(() => {});
    }
  };

  const handleSave = async () => {
    if (!user) {
      setAuthPromptOpen("save");
      return;
    }
    if (!meta.name.trim()) {
      toast.error("Donnez un nom au projet avant de sauvegarder");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: meta.name.trim(),
        client_name: meta.client_name || null,
        address: meta.address || null,
        postal_code: meta.postal_code || null,
        city: meta.city || null,
        pac_id: meta.pac_id || null,
        deperditions_override_kw: meta.deperditions_override_kw ?? null,
        inputs,
      };
      if (isEdit) {
        await updateProject(id, payload);
        toast.success("Projet mis à jour");
      } else {
        const created = await createProject(payload);
        toast.success("Projet enregistré");
        navigate(`/calculator/${created.id}`);
      }
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleExportPdf = () => {
    if (!user) {
      setAuthPromptOpen("export");
      return;
    }
    if (!results) {
      toast.error("Calcul incomplet");
      return;
    }
    const args = {
      projectName: meta.name || "Dimensionnement PAC",
      clientName: meta.client_name,
      address: meta.address,
      postalCode: meta.postal_code,
      city: meta.city,
      pacId: meta.pac_id,
      deperditionsOverrideKw: meta.deperditions_override_kw,
      inputs,
      projectId: isEdit ? id : null,
    };
    const previewUrl = buildPdfPreviewUrl(args);
    const downloadUrl = buildPdfDownloadUrl(args);
    const safeName = (meta.name || "rapport").replace(/[^a-z0-9_\-]/gi, "_");
    setPdfModal({
      open: true,
      previewUrl,
      downloadUrl,
      filename: `PAC_${safeName}.pdf`,
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-6 sm:py-8" data-testid="calculator">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 mb-5 sm:mb-6 flex-wrap">
        <button
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="hidden sm:inline">Tous les projets</span>
          <span className="sm:hidden">Retour</span>
        </button>
        <div className="flex gap-2 ml-auto">
          <Button
            variant="outline"
            onClick={handleExportPdf}
            className="rounded-xl border-slate-200 h-10 px-3 sm:px-4"
            data-testid="export-pdf-btn"
          >
            <FileDown className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Exporter PDF</span>
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#3b82f6] hover:bg-blue-600 text-white rounded-xl h-10 px-3 sm:px-4"
            data-testid="save-project-btn"
          >
            <Save className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">{saving ? "Enregistrement…" : isEdit ? "Mettre à jour" : "Enregistrer"}</span>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left: form */}
        <div className="lg:col-span-7 space-y-6">
          {/* Project meta card */}
          <div className="pac-card p-5 sm:p-6" data-testid="project-meta-card">
            <h2 className="font-display text-lg sm:text-xl font-bold text-slate-900 mb-1">
              {isEdit ? "Modifier le projet" : "Nouveau dimensionnement"}
            </h2>
            <p className="text-sm text-slate-500 mb-4">
              Renseignez les caractéristiques du logement pour obtenir une puissance PAC optimale.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="pac-label">Nom du projet</Label>
                <Input
                  placeholder="Ex. Maison Dupont"
                  value={meta.name}
                  onChange={(e) => setMeta({ ...meta, name: e.target.value })}
                  className="h-11 mt-1.5 bg-white"
                  data-testid="input-project-name"
                />
              </div>
              <div>
                <Label className="pac-label">Client (optionnel)</Label>
                <Input
                  placeholder="Nom du client"
                  value={meta.client_name}
                  onChange={(e) => setMeta({ ...meta, client_name: e.target.value })}
                  className="h-11 mt-1.5 bg-white"
                  data-testid="input-client-name"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="pac-label">Adresse des travaux</Label>
                <AddressAutocomplete
                  value={meta.address}
                  placeholder="N° et nom de rue"
                  onChange={(v) => setMeta((m) => ({ ...m, address: v }))}
                  onSelect={(picked) => {
                    const streetLabel = [picked.housenumber, picked.street]
                      .filter(Boolean)
                      .join(" ")
                      .trim() || picked.label;
                    setMeta((m) => ({
                      ...m,
                      address: streetLabel,
                      postal_code: picked.postcode || m.postal_code,
                      city: picked.city || m.city,
                    }));
                    if (picked.postcode) handlePostalChange(picked.postcode);
                  }}
                />
              </div>
              <div>
                <Label className="pac-label">Code postal</Label>
                <div className="relative mt-1.5">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="ex. 75001"
                    value={meta.postal_code}
                    onChange={(e) => handlePostalChange(e.target.value)}
                    className="h-11 pl-9 bg-white"
                    inputMode="numeric"
                    maxLength={5}
                    data-testid="input-postal-code"
                  />
                </div>
                {inputs.departement && (
                  <p className="text-xs text-emerald-700 mt-1" data-testid="postal-detection">
                    Détecté: {DEPARTEMENT_NAMES[inputs.departement]} ({inputs.departement}) → Zone {inputs.zone}
                  </p>
                )}
              </div>
              <div>
                <Label className="pac-label">Ville</Label>
                <Input
                  placeholder="ex. Paris"
                  value={meta.city}
                  onChange={(e) => setMeta((m) => ({ ...m, city: e.target.value }))}
                  className="h-11 mt-1.5 bg-white"
                  data-testid="input-city"
                />
              </div>
            </div>
          </div>

          {/* Steps tabs */}
          <div className="pac-card p-0 overflow-hidden">
            <div className="flex border-b border-slate-100" data-testid="step-tabs">
              {STEPS.map((s, i) => {
                const Icon = s.icon;
                const active = step === i;
                return (
                  <button
                    key={s.id}
                    onClick={() => setStep(i)}
                    data-testid={`step-tab-${s.id}`}
                    className={`flex-1 px-4 py-3.5 flex items-center justify-center gap-2 text-sm font-medium border-b-2 transition-colors ${
                      active
                        ? "border-blue-500 text-blue-700 bg-blue-50/50"
                        : "border-transparent text-slate-500 hover:text-slate-900"
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="hidden sm:inline">{s.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="p-5 sm:p-6">
              {step === 0 && <BatimentStep inputs={inputs} setInput={setInput} />}
              {step === 1 && (
                <ClimatStep
                  inputs={inputs}
                  setInput={setInput}
                  onSelectZone={handleSelectZone}
                  onSelectDept={handleSelectDept}
                />
              )}
              {step === 2 && (
                <EmetteursStep
                  inputs={inputs}
                  setInput={setInput}
                  results={rangedResults}
                  couvertureRange={couvertureRange}
                  setCouvertureRange={setCouvertureRange}
                />
              )}
              {step === 3 && (
                <PacSelector
                  deperditionsCalcKw={rangedResults?.deperditions_kw ?? 0}
                  deperditionsOverrideKw={meta.deperditions_override_kw}
                  setDeperditionsOverrideKw={(v) =>
                    setMeta((m) => ({ ...m, deperditions_override_kw: v }))
                  }
                  selectedPacId={meta.pac_id}
                  setSelectedPacId={(id) =>
                    setMeta((m) => ({ ...m, pac_id: id }))
                  }
                  couvertureRange={couvertureRange}
                />
              )}
            </div>

            {/* Footer nav */}
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-slate-100 flex items-center justify-between gap-2">
              <Button
                variant="ghost"
                disabled={step === 0}
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                data-testid="step-prev"
                className="px-2 sm:px-4"
              >
                <span className="hidden sm:inline">Précédent</span>
                <ArrowLeft className="h-4 w-4 sm:hidden" />
              </Button>
              <div className="text-xs text-slate-500 font-mono-data flex-shrink-0">
                Étape {step + 1} / {STEPS.length}
              </div>
              {step === STEPS.length - 1 ? (
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 sm:px-4"
                  data-testid="step-validate"
                >
                  <Save className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">{saving ? "Validation…" : isEdit ? "Valider les modifications" : "Valider et enregistrer"}</span>
                  <span className="sm:hidden">Valider</span>
                </Button>
              ) : (
                <Button
                  onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
                  className="bg-[#3b82f6] hover:bg-blue-600 text-white px-3 sm:px-4"
                  data-testid="step-next"
                >
                  Suivant
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Right: results */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-20">
            <ResultsPanel results={rangedResults} />
          </div>
        </div>
      </div>

      <PdfPreviewModal
        open={pdfModal.open}
        previewUrl={pdfModal.previewUrl}
        downloadUrl={pdfModal.downloadUrl}
        filename={pdfModal.filename}
        onClose={() => setPdfModal({ open: false, previewUrl: null, downloadUrl: null, filename: null })}
      />

      {/* Auth prompt modal (when anonymous user tries Save / Export) */}
      {authPromptOpen && (
        <div
          className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center px-4"
          onClick={() => setAuthPromptOpen(null)}
          data-testid="auth-prompt-overlay"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-7"
            onClick={(e) => e.stopPropagation()}
            data-testid="auth-prompt-modal"
          >
            <div className="h-11 w-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <h2 className="mt-4 font-display text-xl font-bold text-slate-900">
              {authPromptOpen === "save"
                ? "Enregistrez ce projet en vous connectant"
                : "Connectez-vous pour exporter le PDF"}
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              Le simulateur reste libre d'utilisation. La création d'un compte
              (1&nbsp;clic via Google) vous permet de retrouver vos projets et
              générer des rapports PDF professionnels.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-2">
              <Button
                onClick={() => {
                  saveDraft({
                    intent: authPromptOpen, // 'save' | 'export'
                    meta,
                    inputs,
                    couvertureRange,
                  });
                  loginWithGoogle("/calculator");
                }}
                className="flex-1 bg-[#3b82f6] hover:bg-blue-600 text-white rounded-xl h-11"
                data-testid="auth-prompt-login-btn"
              >
                <LogIn className="h-4 w-4 mr-2" />
                Continuer avec Google
              </Button>
              <Button
                variant="outline"
                onClick={() => setAuthPromptOpen(null)}
                className="rounded-xl h-11"
                data-testid="auth-prompt-cancel-btn"
              >
                Plus tard
              </Button>
            </div>
            <p className="mt-3 text-[11px] text-slate-400 text-center">
              Aucune donnée n'est partagée — vos projets restent privés.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ----- Step components ----- //

function FieldGrid({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-5">{children}</div>;
}

function BatimentStep({ inputs, setInput }) {
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3 mb-2">
        <div className="h-9 w-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
          <Building2 className="h-5 w-5" />
        </div>
        <div>
          <h3 className="font-display font-bold text-slate-900 text-lg">Caractéristiques du bâtiment</h3>
          <p className="text-sm text-slate-500">Surface, hauteur sous plafond et niveau d'isolation.</p>
        </div>
      </div>
      <FieldGrid>
        <div>
          <Label className="pac-label">Surface chauffée (m²)</Label>
          <Input
            type="number"
            min={10}
            value={inputs.surface}
            onChange={(e) => setInput("surface", parseFloat(e.target.value) || 0)}
            className="h-11 mt-1.5 font-mono-data bg-white"
            data-testid="input-surface"
          />
        </div>
        <div>
          <Label className="pac-label">Hauteur sous plafond (m)</Label>
          <Input
            type="number"
            step="0.1"
            min={2}
            value={inputs.hauteur}
            onChange={(e) => setInput("hauteur", parseFloat(e.target.value) || 0)}
            className="h-11 mt-1.5 font-mono-data bg-white"
            data-testid="input-hauteur"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="pac-label">Niveau d'isolation (norme RT)</Label>
          <Select value={inputs.isolation} onValueChange={(v) => setInput("isolation", v)}>
            <SelectTrigger className="h-11 mt-1.5 bg-white" data-testid="select-isolation">
              <SelectValue placeholder="Choisir un niveau" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(ISOLATION_COEFFICIENTS).map(([key, def]) => (
                <SelectItem key={key} value={key} data-testid={`isolation-option-${key}`}>
                  <span className="flex items-center justify-between gap-4 w-full">
                    <span>{def.label}</span>
                    <span className="font-mono-data text-xs text-slate-500">G = {def.value}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </FieldGrid>
    </div>
  );
}

function ClimatStep({ inputs, setInput, onSelectZone, onSelectDept }) {
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3 mb-2">
        <div className="h-9 w-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
          <ThermometerSun className="h-5 w-5" />
        </div>
        <div>
          <h3 className="font-display font-bold text-slate-900 text-lg">Zone climatique & altitude</h3>
          <p className="text-sm text-slate-500">
            Cliquez sur une zone ou choisissez par département. La température extérieure de base est calculée automatiquement.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="pac-card p-4 bg-slate-50/50">
          <FranceMap
            selectedZone={inputs.zone}
            selectedDept={inputs.departement}
            onSelectZone={onSelectZone}
            onSelectDept={onSelectDept}
            altitude={inputs.altitude}
          />
        </div>
        <DepartmentSelector value={inputs.departement} onSelect={onSelectDept} />
      </div>

      <FieldGrid>
        <div>
          <Label className="pac-label">Zone climatique</Label>
          <Select value={inputs.zone} onValueChange={(v) => setInput("zone", v)}>
            <SelectTrigger className="h-11 mt-1.5 bg-white" data-testid="select-zone">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {["A", "B", "C", "D", "E", "F", "G", "H", "I"].map((z) => (
                <SelectItem key={z} value={z} data-testid={`zone-option-${z}`}>
                  Zone {z}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="pac-label">Altitude (m)</Label>
          <Select
            value={String(inputs.altitude)}
            onValueChange={(v) => setInput("altitude", parseInt(v, 10))}
          >
            <SelectTrigger className="h-11 mt-1.5 bg-white" data-testid="select-altitude">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ALTITUDE_OPTIONS.map((opt) => (
                <SelectItem
                  key={opt.value}
                  value={String(opt.value)}
                  data-testid={`altitude-option-${opt.value}`}
                >
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="pac-label">Température intérieure souhaitée (°C)</Label>
          <Input
            type="number"
            value={inputs.t_interieur}
            onChange={(e) => setInput("t_interieur", parseFloat(e.target.value) || 0)}
            className="h-11 mt-1.5 font-mono-data bg-white"
            data-testid="input-t-interieur"
          />
        </div>
      </FieldGrid>
    </div>
  );
}

function EmetteursStep({ inputs, setInput, results, couvertureRange, setCouvertureRange }) {
  const ranges = [
    { id: "60-80",   label: "60 – 80 %",   sub: "Économique · appoint électrique",      color: "blue"    },
    { id: "80-100",  label: "80 – 100 %",  sub: "Optimal · couverture quasi totale",    color: "emerald" },
    { id: "100-120", label: "100 – 120 %", sub: "Sécurisé · marge de sécurité max.",    color: "amber"   },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3 mb-2">
        <div className="h-9 w-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
          <Zap className="h-5 w-5" />
        </div>
        <div>
          <h3 className="font-display font-bold text-slate-900 text-lg">Type d'émetteur</h3>
          <p className="text-sm text-slate-500">
            La température de départ influence directement le COP de votre PAC.
          </p>
        </div>
      </div>

      {/* Coefficient de surpuissance réglementaire — 3 plages */}
      <div data-testid="coverage-range-selector">
        <div className="flex items-baseline justify-between mb-2 flex-wrap gap-1">
          <Label className="pac-label">Coefficient de surpuissance réglementaire</Label>
          <span className="text-xs text-slate-500">
            Plage de couverture des déperditions
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {ranges.map((r) => {
            const active = couvertureRange === r.id;
            const tone = {
              blue:    { border: "border-blue-500",    bg: "bg-blue-50/60",    accent: "text-blue-700"    },
              emerald: { border: "border-emerald-500", bg: "bg-emerald-50/60", accent: "text-emerald-700" },
              amber:   { border: "border-amber-500",   bg: "bg-amber-50/60",   accent: "text-amber-700"   },
            }[r.color];
            return (
              <button
                type="button"
                key={r.id}
                onClick={() => setCouvertureRange(r.id)}
                data-testid={`coverage-range-${r.id}`}
                className={`text-left rounded-xl p-3 border-2 transition-all ${
                  active
                    ? `${tone.border} ${tone.bg}`
                    : "border-slate-200 bg-white hover:border-slate-300"
                }`}
              >
                <div className={`font-mono-data text-base font-bold ${active ? tone.accent : "text-slate-900"}`}>
                  {r.label}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">{r.sub}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="emetteur-options">
        {Object.entries(EMETTEURS).map(([key, em]) => {
          const active = inputs.emetteur === key;
          return (
            <button
              type="button"
              key={key}
              onClick={() => setInput("emetteur", key)}
              data-testid={`emetteur-${key}`}
              className={`text-left rounded-2xl p-4 border-2 transition-all ${
                active
                  ? "border-blue-500 bg-blue-50/50"
                  : "border-slate-100 bg-white hover:border-slate-300"
              }`}
            >
              <div className="font-mono-data text-xs uppercase tracking-wider text-blue-600">
                {em.temp}
              </div>
              <div className="mt-1 font-display font-semibold text-slate-900">{em.label}</div>
              <div className="text-xs text-slate-500 mt-1">{em.desc}</div>
            </button>
          );
        })}
      </div>

      <HeatingCurve
        t_base={results?.t_base ?? -7}
        t_interieur={inputs.t_interieur ?? 20}
        selectedEmetteur={inputs.emetteur}
      />
    </div>
  );
}
